import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

/** Pantalla táctil pequeña: en móvil los PDFs/adjuntos se abren en pestaña
 *  nueva (visor nativo) — el visor embebido y los blobs secuestran la
 *  pestaña en iOS y "atrás" deja la app bloqueada (2026-07-25). */
export function esMovil() {
  return (typeof window !== 'undefined')
    && (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 640);
}

/** ¿Corriendo como app instalada desde el icono de la pantalla de inicio?
 *
 * Ese modo («standalone») es un WKWebView desnudo: sin barra de Safari, sin
 * gestor de descargas y sin el visor de documentos Office que sí tiene Safari.
 * Un archivo que no sea imagen/PDF (un .docx, por ejemplo) se abre en pestaña
 * nueva en Safari sin problema, pero dentro del icono no pasa NADA al
 * tocarlo — ni error, ni descarga, silencio total (2026-07-28).
 */
export function esStandalone() {
  if (typeof window === 'undefined') return false;
  return window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
}

/** URL absoluta que, desde el icono de la pantalla de inicio, se abre en
 *  SAFARI de verdad en vez de quedarse muda dentro del icono. `x-safari-` es
 *  el esquema que iOS reconoce para forzar ese salto; fuera de standalone no
 *  hace falta —Safari ya sabe abrir cualquier cosa por su cuenta. */
export function urlParaAbrirFuera(rutaOAbsoluta) {
  const absoluta = /^https?:\/\//i.test(rutaOAbsoluta)
    ? rutaOAbsoluta
    : `${window.location.origin}${rutaOAbsoluta}`;
  return esStandalone() ? `x-safari-${absoluta}` : absoluta;
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}


/** Los nueve dígitos españoles, agrupados como se leen por teléfono. */
function espanol(nueve) {
  return `+34 ${nueve.slice(0, 3)} ${nueve.slice(3, 5)} ${nueve.slice(5, 7)} ${nueve.slice(7, 9)}`;
}

/** Los prefijos de país de DOS cifras — la lista entera de la ITU. Fuera de
 *  ella solo caben los de UNA (los que empiezan por 1 o por 7: Norteamérica,
 *  Rusia y Kazajistán) y los de TRES, que son todo lo demás. */
const PREFIJOS_DE_DOS_CIFRAS = new Set([
  '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44',
  '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58',
  '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '90', '91',
  '92', '93', '94', '95', '98',
]);

function cifrasDelPrefijo(cifras) {
  if (cifras.startsWith('1') || cifras.startsWith('7')) return 1;
  return PREFIJOS_DE_DOS_CIFRAS.has(cifras.slice(0, 2)) ? 2 : 3;
}

/** Extranjero: el «+» pegado al prefijo y DETRÁS la separación que escribió
 *  quien lo apuntó («+44 7700 900123»), sin reagrupar nada — quien apuntó el
 *  número sabe cómo se lee en su país.
 *
 *  Si vino de corrido no hay separación que respetar, y entonces sí hay que
 *  poner una: muchos extranjeros están guardados sin ningún hueco y salían
 *  como un churro de cifras en el que nadie distingue el país (2026-09-15). Se despega
 *  el prefijo con UN espacio y el resto se deja de una pieza: cada país
 *  agrupa a su manera y equivocarse ahí es peor que dejarlo entero. */
function internacional(raw) {
  const resto = raw.slice(1).replace(/^[\s\-./]+/, '');
  // Mismísima pregunta que el backend (`re.fullmatch(r"[0-9]+", resto)`): si
  // lo que sigue al «+» no son solo cifras, es separación de quien lo apuntó.
  // Preguntar por una lista de signos, como se hacía aquí, dejaba fuera la
  // coma o una letra, y entonces el papel y la pantalla escribían distinto el
  // mismo número.
  if (!/^[0-9]+$/.test(resto)) return `+${resto.replace(/\s+/g, ' ').trim()}`;
  const pais = cifrasDelPrefijo(resto);
  if (resto.length <= pais) return `+${resto}`;
  return `+${resto.slice(0, pais)} ${resto.slice(pais)}`;
}

/**
 * Teléfono para ENSEÑAR, con su prefijo (2026-08-19: los extranjeros
 * salían sin prefijo en el cuadrito de la cita). Mismo criterio, línea por
 * línea, que el PDF del parte (backend/services/telefonos.py,
 * `con_prefijo` — antes vivía en agenda_dia_pdf_generator.py): +34 agrupado
 * si es nacional, se respeta el
 * internacional (+.. / 00..), y lo que no se reconoce, tal cual llegó.
 *
 * Es para MIRARLO, no para enlazarlo: lleva espacios a propósito. Antes
 * acababa en `'+' + digits` y el cuadrito de la cita enseñaba
 * «+447700900123», trece cifras de corrido en las que nadie distingue dónde
 * acaba el país (2026-09-11). Para el `href` de llamar o de
 * WhatsApp, `telefonoParaEnlace`.
 *
 * Lo que NO se toca: el español de 9 cifras (con o sin 34 delante) y el que
 * llega sin «+» y no son nueve cifras españolas — ahí no se inventa el país
 * de nadie, que en las fichas puede haber números de diez cifras sueltos.
 */
export function telefonoConPrefijo(phone) {
  let raw = String(phone || '').trim();
  if (!raw) return '';
  // Un teléfono con coletilla («600000000 (madre)») se deja ENTERO: rearmarlo
  // se come la palabra, y saber de quién es el número no es un adorno.
  if (/\p{L}/u.test(raw)) return raw;
  let digits = raw.replace(/[^0-9]/g, '');
  // «0044 …» es el mismo prefijo internacional escrito a la europea.
  if (raw.startsWith('00') && digits.length > 11) {
    raw = `+${raw.slice(2).replace(/^[\s\-./]+/, '')}`;
    digits = digits.slice(2);
  }
  if (raw.startsWith('+')) {
    if (digits.startsWith('34') && digits.length === 11) return espanol(digits.slice(2));
    return internacional(raw);
  }
  if (digits.length === 9) return espanol(digits);
  if (digits.startsWith('34') && digits.length === 11) return espanol(digits.slice(2));
  return raw;
}

/**
 * El MISMO teléfono, pero para un `href` (`tel:` o `wa.me`): sin espacios ni
 * guiones, que ahí no pintan nada. Conserva el «+» del internacional y pasa
 * «00…» a «+…»; a los nueve dígitos españoles no les inventa prefijo porque
 * el marcador de casa ya sabe llamarlos.
 */
export function telefonoParaEnlace(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (raw.startsWith('00') && digits.length > 11) return `+${digits.slice(2)}`;
  return raw.startsWith('+') ? `+${digits}` : digits;
}


/** 'smooth' salvo que el sistema pida menos movimiento (la API JS no lo
 *  respeta sola, hay que ramificar) — auditoría de animación 2026-08-21. */
export function desplazamiento() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

/** Búsqueda sin castigo: minúsculas y sin tildes («jose garcia» encuentra a
 *  «José García»). Para TODOS los filtros de pantalla (2026-08-27). */
export function normalizaTexto(s) {
  return (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
